import React, {createContext, PropsWithChildren, useContext, useEffect, useMemo, useRef} from "react"
import qs from "querystring"
import moment from "moment"
import {Result, ResultAsync} from "neverthrow"
import {useEvent, useLocalStorage, useSessionStorage} from "react-use"
import useAsyncResultIdle from "../hooks/useAsyncResultIdle"
import {AsyncResultIdleRequestState} from "../types/asyncResultIdle"
import {post, UNAUTHORIZED_EVENT} from "../utilities/http"
import {assertIsDefined} from "../utilities/assert"
import {useCookies} from "react-cookie"
import {parse} from "tldts"
import {isUndefined} from "lodash"
import {ApiTokenPresent} from "../resources/apiToken"
import {deleteResource} from "../resources/common"
import {useToasts} from "react-toast-notifications"
import {claimsContext} from "./claims"
import * as webauthnJson from "@github/webauthn-json/browser-ponyfill"
import {logoutZendesk} from "../hooks/zendeskWidget"

export type AuthenticateFn = (
	username: string,
	password: string,
	assessmentToken: string
) => Promise<Result<{}, AuthError>>

export const enum UserType {
	Unit = "unit",
	Org = "org",
	Bank = "bank",
}

export function isUnitUser(userType: string | undefined): userType is UserType.Unit {
	return userType === UserType.Unit
}

export function isOrgUser(userType: string | undefined): userType is UserType.Org {
	return userType === UserType.Org
}

export function isBankUser(userType: string | undefined): userType is UserType.Bank {
	return userType === UserType.Bank
}

export function useUserEmail(userClaims?: Claims) {
	const claims = userClaims ?? useContext(claimsContext)
	assertIsDefined(claims)
	return claims.sub
}

export function useOrgUserOrgId(userClaims?: Claims) {
	const claims = userClaims ?? useContext(claimsContext)
	assertIsDefined(claims)
	return claims.orgId
}

export function useBankUserBankId(userClaims?: Claims) {
	const claims = userClaims ?? useContext(claimsContext)
	assertIsDefined(claims)
	return claims.bankId
}

export type Claims = {
	userType: UserType
	role: string
	roles?: string[]
	userId: string
	org: string
	orgId: string
	bankId: string
	isUnitPilot: boolean
	sub: string
}

export type Token = {
	type: TokenType
	expires: Date
	accessToken: string
	isSSO: boolean
}

export type TwoFactorToken = {
	type: TokenType
	verificationToken: string
	expires: Date
	phone: string
	userId?: number
}

export enum TokenType {
	TwoFactorToken = "2fa",
	AccessToken = "bearer",
}

export type AuthError = {
	error: string
	code?: string
}
type AuthContextState = [
	Token | undefined,
	(token: Token | undefined) => void,
	() => void,
	(onBehalfToken: Token | undefined) => void
]

const authContext = createContext<AuthContextState>([
	undefined,
	(_token: Token | undefined) => {
		// no context yet
	},
	() => {
		// no remove function
	},
	(_onBehalfToken: Token | undefined) => {
		// no context yet
	},
])

export function AuthProvider({children}: PropsWithChildren<{}>) {
	const [storedToken, setToken, removeToken] = useLocalStorage<Token | undefined>("token", getTokenFromCookie())
	const [onBehalfToken, setOnBehalfToken] = useSessionStorage<Token | undefined>("onBehalfToken")

	const token = useMemo(() => {
		if (onBehalfToken && moment(onBehalfToken.expires).isAfter()) {
			return onBehalfToken
		} else if (storedToken && moment(storedToken.expires).isAfter()) {
			return storedToken
		} else {
			return undefined
		}
	}, [storedToken, onBehalfToken])

	const timeout = useRef<ReturnType<typeof setTimeout>>()
	const [, , removeTutorialToken] = useLocalStorage<ApiTokenPresent | undefined>("tutorialToken")

	useEffect(() => {
		if (isUndefined(token)) {
			removeTutorialToken()
			removeToken()
			setOnBehalfToken(undefined)
		}
	}, [token])

	useEvent("storage", (event: StorageEvent) => {
		//For impersonate feature if in one tab there was log out we want to log out from all the tabs
		if (event.key === "token" && event.newValue === null) {
			removeToken()
			setOnBehalfToken(undefined)
		}
	})

	// On any 401 from the API (session invalid server-side), log the user out so they are redirected to login.
	useEvent(UNAUTHORIZED_EVENT, () => {
		removeToken()
		setOnBehalfToken(undefined)
	})

	function expireToken() {
		// TODO: Re-login if no-inactivity?
		setOnBehalfToken(undefined)
		removeToken()
	}

	function getTokenFromCookie() {
		const domain = () => {
			const parsedUrl = parse(window.location.hostname)
			return parsedUrl.domain ? parsedUrl.domain : window.location.hostname
		}

		const [cookies, , removeCookie] = useCookies(["dash_access_data"])
		try {
			const tokenData = cookies.dash_access_data
			return {
				type: TokenType.AccessToken,
				accessToken: "",
				expires: moment()
					.add(tokenData.expires_in - 60, "seconds")
					.toDate(),
				isSSO: tokenData.is_sso,
			}
		} catch (err) {
		} finally {
			removeCookie("dash_access_data", {domain: domain(), path: "/"})
		}
		return undefined
	}

	useEffect(() => {
		if (timeout.current) {
			clearTimeout(timeout.current)
			timeout.current = undefined
		}

		if (token) timeout.current = setTimeout(expireToken, moment(token.expires).diff(moment()))
	}, [token])

	useEffect(() => {
		return () => {
			if (timeout.current) {
				clearTimeout(timeout.current)
				timeout.current = undefined
			}
		}
	}, [])

	return (
		<authContext.Provider value={[token, setToken, removeToken, setOnBehalfToken]}>{children}</authContext.Provider>
	)
}

function getWebauthnCredentialPromise(response: WebauthnAuthenticateResponse) {
	const assertionOptions = JSON.parse(response.data.attributes.publicKey)
	return webauthnJson.get(webauthnJson.parseRequestOptionsFromJSON(assertionOptions))
}

function mapWebauthnError(e: unknown): AuthError {
	const errorObj = e as {message?: string}
	return {
		error: errorObj?.message ?? "Authentication Failure",
		code: "400",
	}
}

export function useWebauthnAuthentication(): [
	AsyncResultIdleRequestState<string, AuthError>,
	(username: string) => Promise<Result<string, AuthError>>,
	AsyncResultIdleRequestState<Token, AuthError>,
	(username: string, credentialResponseJson: string) => Promise<Result<Token, AuthError>>
] {
	const [, setToken] = useContext(authContext)
	const [startState, startAuthenticate] = useAsyncResultIdle(async function (username: string) {
		const headers = {"Content-Type": "application/vnd.api+json"}
		const startAuthenticationResponse = await post<WebauthnAuthenticateResponse, ErrorResponse>(
			`${process.env.API_URL}/token/webauthn/authenticate/start`,
			{
				data: {
					attributes: {
						username: username.trim(),
					},
				},
			},
			{headers}
		)

		return startAuthenticationResponse
			.asyncAndThen((response) => ResultAsync.fromPromise(getWebauthnCredentialPromise(response), mapWebauthnError))
			.map(JSON.stringify)
			.mapErr((e) => {
				return {error: e.error, code: e.code}
			})
	}, [])

	const [finishState, finishAuthenticate] = useAsyncResultIdle(async function (
		username: string,
		credentialResponseJson: string
	) {
		const headers = {"Content-Type": "application/vnd.api+json"}
		const finishAuthenticateResponse = await post<accessTokenResponse, ErrorResponse>(
			`${process.env.API_URL}/token/webauthn/authenticate/finish`,
			{
				data: {
					attributes: {
						username: username.trim(),
						credentialResponseJson: credentialResponseJson,
					},
				},
			},
			{headers}
		)

		return finishAuthenticateResponse
			.asyncMap(async (authResult) => {
				const token = {
					type: TokenType.AccessToken,
					accessToken: "",
					expires: moment()
						.add(authResult.expires_in - 60, "seconds")
						.toDate(),
					isSSO: authResult.is_sso,
				}
				setToken(token)
				return token
			})
			.mapErr((e) => {
				return {
					error: e.errors[0]?.title || "Authentication Failure",
					code: e.errors[0]?.status || "400",
				}
			})
	},
	[])

	return [startState, startAuthenticate, finishState, finishAuthenticate]
}

export function useTwoFactorAuthentication(): [
	AsyncResultIdleRequestState<Token, AuthError>,
	(code: string, twoFactorToken: string, userId?: number) => Promise<Result<Token, AuthError>>,
	AsyncResultIdleRequestState<TwoFactorToken, AuthError>,
	(twoFactorToken: string, userId?: number) => Promise<Result<TwoFactorToken, AuthError>>
] {
	const [, setToken] = useContext(authContext)

	const [state, authenticate] = useAsyncResultIdle(async function (
		code: string,
		twoFactorToken: string,
		userId?: number
	) {
		const config = {
			headers: {
				"Content-Type": "application/x-www-form-urlencoded",
			},
		}
		const data = qs.stringify({verificationToken: twoFactorToken, code: code, userId: userId})
		const response = await post<SuccessResponse, ErrorResponse>(
			`${process.env.API_URL}/token/checkVerification`,
			data,
			config
		)
		return response
			.asyncMap(async (authResult) => {
				switch (authResult.token_type) {
					case TokenType.AccessToken:
						const token: Token = {
							type: TokenType.AccessToken,
							accessToken: "",
							expires: moment()
								.add(authResult.expires_in - 60, "seconds")
								.toDate(),
							isSSO: authResult.is_sso,
						}
						setToken(token)
						return token
					case TokenType.TwoFactorToken:
						throw new Error("2fa token not expected")
				}
			})
			.mapErr((e) => {
				if (e.errors) {
					return {error: e.errors.find((m) => !isUndefined(m.title))?.title || "Authentication Failure"}
				} else {
					return e.error_description
						? {error: e.error_description, code: e.code}
						: {
								error: e.error,
								code: e.code,
						  }
				}
			})
	},
	[])

	const [resendState, resend] = useAsyncResultIdle(async function (twoFactorToken: string, userId?: number) {
		const config = {
			headers: {
				"Content-Type": "application/x-www-form-urlencoded",
			},
		}
		const data = qs.stringify({verificationToken: twoFactorToken, userId: userId})
		const response = await post<SuccessResponse, ErrorResponse>(
			`${process.env.API_URL}/token/createVerification`,
			data,
			config
		)
		return response
			.asyncMap(async (authResult) => {
				switch (authResult.token_type) {
					case TokenType.AccessToken:
						throw new Error("bearer token not expected")
					case TokenType.TwoFactorToken:
						return {
							type: TokenType.TwoFactorToken,
							verificationToken: authResult.verification_token,
							expires: moment()
								.add(authResult.expires_in - 60, "seconds")
								.toDate(),
							phone: authResult.phone,
							userId: authResult.user_id,
						}
				}
			})
			.mapErr((e) => {
				if (e.errors) {
					return {error: e.errors.find((m) => !isUndefined(m.title))?.title || "Authentication Failure"}
				} else {
					return e.error_description
						? {error: e.error_description, code: e.code}
						: {
								error: e.error,
								code: e.code,
						  }
				}
			})
	})

	return [state, authenticate, resendState, resend]
}

export function useAuthentication(): [AsyncResultIdleRequestState<Token | TwoFactorToken, AuthError>, AuthenticateFn] {
	const [, setToken] = useContext(authContext)

	const [state, authenticate] = useAsyncResultIdle(async function (
		username: string,
		password: string,
		assessmentToken: string
	) {
		const config = {
			headers: {
				"Content-Type": "application/x-www-form-urlencoded",
			},
			auth: {
				username: username.trim(),
				password: password,
				assessmentToken: assessmentToken,
			},
		}

		const data = qs.stringify({grant_type: "client_credentials", assessment_token: assessmentToken})
		const response = await post<SuccessResponse, ErrorResponse>(`${process.env.API_URL}/token`, data, config)

		return response
			.asyncMap(async (authResult) => {
				switch (authResult.token_type) {
					case TokenType.AccessToken:
						const token = {
							type: TokenType.AccessToken,
							accessToken: "",
							expires: moment()
								.add(authResult.expires_in - 60, "seconds")
								.toDate(),
							isSSO: authResult.is_sso,
						}
						setToken(token)
						return token
					case TokenType.TwoFactorToken:
						return {
							type: TokenType.TwoFactorToken,
							verificationToken: authResult.verification_token,
							expires: moment()
								.add(authResult.expires_in - 60, "seconds")
								.toDate(),
							phone: authResult.phone,
							userId: authResult.user_id,
						}
				}
			})
			.mapErr((e) => {
				if (e.errors) {
					return {error: e.errors.find((m) => !isUndefined(m.title))?.title || "Authentication Failure"}
				} else {
					return e.error_description
						? {error: e.error_description, code: e.code}
						: {
								error: e.error,
								code: e.code,
						  }
				}
			})
	},
	[])

	return [state, authenticate]
}

export function useLogout() {
	const token = useAccessToken()
	const [, , removeToken, setOnBehalfToken] = useContext(authContext)
	const [, , remove] = useLocalStorage<ApiTokenPresent | undefined>("tutorialToken")
	const {addToast} = useToasts()

	return function () {
		deleteResource("token", token).then((result) => {
			if (result.isOk()) {
				logoutZendesk()

				remove()
				removeToken()
				setOnBehalfToken(undefined)
			} else {
				addToast("Something went wrong", {appearance: "error"})
			}
		})
	}
}

export function useIsAuthenticated(): boolean {
	const [token] = useContext(authContext)
	return !!token && token.type === TokenType.AccessToken
}

export function useIsSSOAuthenticated(): boolean {
	const [token] = useContext(authContext)
	return !!token && token.isSSO
}

export function useAccessToken() {
	const [token] = useContext(authContext)
	assertIsDefined(token)
	return token.accessToken
}

export function useSetOnBehalfToken() {
	const [, , , setOnBehalfToken] = useContext(authContext)
	return setOnBehalfToken
}

export function useUserType(userClaims?: Claims) {
	const claims = userClaims ?? useContext(claimsContext)
	assertIsDefined(claims)

	if (claims.userType) return claims.userType
	else {
		switch (claims.role) {
			case "super":
				return UserType.Unit
			case "compliance":
				return UserType.Unit
			case "compliance-readonly":
				return UserType.Unit
			case "implementations":
				return UserType.Unit
			case "admin":
				return UserType.Org
			default:
				return claims.role
		}
	}
}

export function useUserRole(userClaims?: Claims) {
	const claims = userClaims ?? useContext(claimsContext)
	assertIsDefined(claims)
	return claims.role
}

export function useUserId() {
	const claims = useContext(claimsContext)
	assertIsDefined(claims)
	return claims.userId
}

export function useIsOrgUser(claims?: Claims) {
	const type = useUserType(claims)
	return isOrgUser(type)
}

export function useIsBankUser(claims?: Claims) {
	const type = useUserType(claims)
	return isBankUser(type)
}

export function useIsPartnerUser() {
	const type = useUserType()
	return type === "partner"
}

export function useIsOnBehalfUser() {
	const type = useUserType()
	return type === "partnerOnBehalf"
}

export function useIsItUser() {
	const role = useUserRole()
	return role === "it"
}

function isTokenMatchesRole(role?: string, userClaims?: Claims) {
	const claims = userClaims ?? useContext(claimsContext)
	assertIsDefined(claims)
	return !role || claims.role === role
}

export function useIsBankAdminUser() {
	const userType = useUserType()
	return isBankUser(userType) && isTokenMatchesRole("bank")
}

export function useIsBankAdminOrOperationsUser() {
	const userType = useUserType()
	return isBankUser(userType) && (isTokenMatchesRole("bank-operations") || isTokenMatchesRole("bank"))
}

export function useIsBankOperationsUser() {
	const userType = useUserType()
	return isBankUser(userType) && isTokenMatchesRole("bank-operations")
}

export function useIsBankReadOnlyUser() {
	const userType = useUserType()
	return isBankUser(userType) && isTokenMatchesRole("bank-readonly")
}

export function useIsUnitTypeUser(role?: string, claims?: Claims) {
	const userType = useUserType(claims)
	return isUnitUser(userType) && isTokenMatchesRole(role, claims)
}

export function useIsUnitSuperUser() {
	return useIsUnitTypeUser("super")
}

export function useIsUnitAdminUser(claims?: Claims) {
	return (
		useIsUnitTypeUser("super", claims) ||
		useIsUnitTypeUser("compliance", claims) ||
		useIsUnitTypeUser("unit-admin", claims)
	)
}

export function useIsUnitAdminReadonlyUser(claims?: Claims) {
	return (
		useIsUnitTypeUser("super", claims) ||
		useIsUnitTypeUser("compliance", claims) ||
		useIsUnitTypeUser("compliance-readonly", claims) ||
		useIsUnitTypeUser("unit-admin", claims)
	)
}

export function useIsUnitReadonlyUser(claims?: Claims) {
	return useIsUnitTypeUser("unit-readonly", claims)
}

export function useIsUnitSalesUser() {
	return useIsUnitTypeUser("unit-sales")
}

export function useIsImplementationsUser() {
	return useIsUnitTypeUser("implementations")
}

export function useIsUnitComplianceUser() {
	return useIsUnitTypeUser("compliance")
}

export function useIsUnitUser(claims?: Claims) {
	return useIsUnitTypeUser(undefined, claims)
}

export function useIsOrgAdmin() {
	const claims = useContext(claimsContext)
	assertIsDefined(claims)
	return claims.role === "admin"
}

const ORG_MANAGED_SOLUTIONS_BANKING_ROLES = [
	"org_banking_rtl_admin",
	"org_banking_rtl_operational_admin",
	"org_banking_rtl_basic",
]

// Some environments populate `claims.roles[]`, others only set the singular `claims.role`,
// and `claims.roles` may be present but empty. Always consider both so callers don't have
// to know which shape the backend returned.
function getAllClaimRoles(claims: Claims): string[] {
	const rolesArray = Array.isArray(claims.roles) ? claims.roles : []
	return [...rolesArray, claims.role].filter((r): r is string => Boolean(r))
}

export function useIsManagedSolutionsBankingUser() {
	const claims = useContext(claimsContext)
	assertIsDefined(claims)
	return getAllClaimRoles(claims).some((role) => ORG_MANAGED_SOLUTIONS_BANKING_ROLES.includes(role))
}

// Mirrors the backend `isOperationalAdminOnly` check in ManagedSolutionsApi.scala:
// true when the user has `org_banking_rtl_operational_admin` but NOT `org_banking_rtl_admin`,
// i.e. they are limited to operational-account resources only.
export function useIsOrgManagedSolutionsOperationalAdminOnly() {
	const claims = useContext(claimsContext)
	assertIsDefined(claims)
	const roles = getAllClaimRoles(claims)
	return roles.includes("org_banking_rtl_operational_admin") && !roles.includes("org_banking_rtl_admin")
}

export function useIsOrgOrUnitAdminUser() {
	return useIsUnitAdminUser() || useIsOrgAdmin()
}

export function useIsOrgUnitPilot() {
	const claims = useContext(claimsContext)
	assertIsDefined(claims)
	return claims.isUnitPilot
}

export function useIsUnitAdminOrgAdminBankOperationsOnly() {
	return useIsUnitAdminUser() || useIsOrgAdmin() || useIsBankAdminOrOperationsUser()
}

export function useUserClaimsData(userClaims?: Claims) {
	const claims = userClaims ?? useContext(claimsContext)
	assertIsDefined(claims)
	return claims
}

export function useIsUser(userId: string) {
	return useUserId() === userId
}

type accessTokenResponse = {
	access_token: string
	expires_in: number
	token_type: TokenType.AccessToken
	is_sso: boolean
}

type twoFactorTokenResponse = {
	verification_token: string
	token_type: TokenType.TwoFactorToken
	expires_in: number
	phone: string
	user_id?: number
}

type WebauthnAuthenticateResponse = {
	data: {
		attributes: {
			publicKey: string
		}
	}
}

type SuccessResponse = accessTokenResponse | twoFactorTokenResponse

type ErrorResponse = {
	error: string
	errors: Array<{status: string; title: string}>
	error_description: string
	code?: string
}
